import { bbox } from '@turf/turf';
import type { Geometry } from 'geojson';
import type { LngLatBoundsLike } from 'maplibre-gl';
import { useSearchParams } from 'next/navigation';
import { type MutableRefObject, useCallback, useEffect, useMemo } from 'react';

import { useInsightsCoverageContext } from '@/features/insightsCoverage/provider';
import { useOnFirstRenderOnly } from '@/hooks/useOnFirstRenderOnly';
import { useMapboxContext } from '@/providers/mapbox/MapboxProvider';
import getCenterOfStateCountyGeometry from '@/utils/flyToLocation/flyToStateCounty';

interface PermsStatus {
  state: undefined | string;
}

export const useHandleInitialMapboxZooming = (
  mapContainer: MutableRefObject<HTMLDivElement | null>
) => {
  const searchParams = useSearchParams();

  const { isMapInstanceLoaded, mapInstance: map } = useMapboxContext();

  const { insightsCoverage } = useInsightsCoverageContext();

  const { countyInfo, stateInfo } = insightsCoverage;

  const mapContainerLoaded = useMemo(() => {
    return !!mapContainer;
  }, [mapContainer]);

  const handleInitialMapboxZooming = useCallback(async () => {
    if (!map || !isMapInstanceLoaded) return;

    const hasLocationPerms: PermsStatus = { state: undefined };
    if (navigator.permissions) {
      const val = await navigator.permissions.query({
        name: 'geolocation',
      });

      hasLocationPerms.state = val.state;
    }

    const zoomToGeometry = async (geometry: Geometry) => {
      const data = getCenterOfStateCountyGeometry(geometry);

      if (data.geometry) {
        map.fitBounds(bbox(data.geometry) as LngLatBoundsLike, {
          animate: false,
          padding: {
            bottom: 50,
            left: 100,
            right: 200,
            top: 100,
          },
        });
      } else {
        map.flyTo({
          center: [data.center.lng, data.center.lat],
          zoom: 13.5,
        });
      }
    };

    if (searchParams?.get('sharecode')) return;

    if (countyInfo) {
      zoomToGeometry(countyInfo.geometry);
      return;
    }

    if (stateInfo) {
      zoomToGeometry(stateInfo.geometry);
      return;
    }

    if (hasLocationPerms.state === 'granted') {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          map.flyTo({
            center: [position.coords.longitude, position.coords.latitude],
            zoom: 13.5,
          });
        },
        () => {},
        {
          enableHighAccuracy: false,
          maximumAge: 60000,
          timeout: 500,
        }
      );
    }
  }, [map, isMapInstanceLoaded, countyInfo, stateInfo, searchParams]);

  const runOnce = useOnFirstRenderOnly();

  useEffect(() => {
    if (!map || !isMapInstanceLoaded || !mapContainerLoaded) return;

    runOnce?.(handleInitialMapboxZooming);
  }, [
    handleInitialMapboxZooming,
    isMapInstanceLoaded,
    map,
    mapContainerLoaded,
    runOnce,
  ]);
};
